/**
 * Performance budgets and
 * resource-limit configuration for the parser pipeline.
 *
 * Two surfaces live here:
 *
 *  1. {@link PERF_BUDGETS} — wall-clock budgets per pipeline stage. They
 *  are advisory (the parser does NOT abort on overrun); perf tests
 *  assert against them so a regression that pushes a stage above its
 *  budget surfaces in CI. CI relaxes thresholds 30 % — the goal is to
 *  catch real algorithmic regressions, not flake on a noisy runner.
 *
 *  2. {@link ParserConfig} — resource limits the parser enforces. When a
 *  limit trips the parser emits a typed `Defect` (severity `warning`)
 *  and continues with partial output. The defaults mirror the v3
 *  spec's "permissive but bounded" stance so today's fixtures keep
 *  parsing while the limits exist as a containment for future
 *  adversarial / multi-GB inputs.
 *
 * Both surfaces are intentionally pure data — no side effects, no
 * imports from the rest of the parser. They are imported into
 * `src/perf/parse.bench.test.ts`, `src/email-parser.ts`, and the
 * Settings → Limits tab.
 *
 * The two persistence helpers below are the exception, and the only
 * one. `writeParserConfig` reaches `utils/storageError` for the same
 * classification `persist.ts` uses; nothing in the parser calls it, so
 * it leaves the engine chunk on the first tree shake.
 */

import { errorLog } from "../utils/errorLog";
import {
  classifyStorageError,
  emitStorageToast,
  type StorageWriteResult,
} from "../utils/storageError";

export type { StorageWriteResult };

/**
 * Wall-clock budgets per pipeline stage. Numbers are in milliseconds
 * and reflect the v3 §29 targets:
 *
 *  - `parseMs` — `parseEmail` end-to-end on a 1 MB synthetic.
 *  - `analyzeMs` — `analyzeEmail` end-to-end on the parsed shape.
 *  - `renderMs` — first paint of the Overview tab body.
 *  - `searchMs` — global-search keystroke → result list.
 *  - `attachmentHashMs` — SHA-256 of a single attachment off the worker.
 *  - `permalinkEncodeMs` — encode the active message into a permalink.
 *
 * iter254 measured ~34 ms parse+analyze on a healthy M-series laptop;
 * the budgets are set generously (~2-3x measured) so they catch real
 * regressions without flaking on slow shared CI runners.
 */
export const PERF_BUDGETS = {
  parseMs: 80,
  analyzeMs: 50,
  renderMs: 50,
  searchMs: 16,
  attachmentHashMs: 200,
  permalinkEncodeMs: 500,
} as const;

/** Union of every budget key in {@link PERF_BUDGETS}. */
export type PerfBudgetKey = keyof typeof PERF_BUDGETS;

/**
 * CI multiplier — perf tests scale every threshold by this factor when
 * `process.env.CI` is set. The goal is to catch ~5x regressions while
 * tolerating the ~30 % variance shared CI runners exhibit.
 */
export const CI_BUDGET_RELAX_MULTIPLIER = 1.3;

/**
 * Resolve a perf budget for the current environment (CI or local).
 * Returns the configured budget, multiplied by
 * {@link CI_BUDGET_RELAX_MULTIPLIER} when `CI` is in `process.env`.
 *
 * Pure helper so tests can inject a synthetic env without monkey-
 * patching `process`.
 */
export function resolveBudget(
  key: PerfBudgetKey,
  env: { CI?: string } = typeof process !== "undefined"
    ? (process.env as { CI?: string })
    : {},
): number {
  const base = PERF_BUDGETS[key];
  return env.CI ? base * CI_BUDGET_RELAX_MULTIPLIER : base;
}

/**
 * Resource limits + feature toggles applied during parse. Mirrors v3
 * §05 `ParserConfig` shape. Defaults are permissive enough to parse
 * every existing fixture but bounded so adversarial / multi-GB inputs
 * surface as defects rather than locking up the tab.
 */
export type ExternalNetworkPolicy = "disabled" | "dns-only" | "allowlisted";

export interface ParserConfig {
  /** Max bytes accepted as a single message (50 MB). */
  maxMessageBytes: number;
  /** Max bytes for the header block (1 MB). */
  maxHeaderBytes: number;
  /** Max number of header fields (10 000). */
  maxHeaderCount: number;
  /** Max nested MIME depth (50). */
  maxMimeDepth: number;
  /** Max number of MIME parts in the tree (10 000). */
  maxMimePartCount: number;
  /** Max bytes per decoded part body (50 MB). */
  maxDecodedPartBytes: number;
  /** Max bytes per extracted attachment (50 MB). */
  maxAttachmentBytes: number;
  /** Max archive nesting depth (3). */
  maxArchiveDepth: number;
  /** Max embedded `message/rfc822` nesting depth (10). */
  maxEmbeddedDepth: number;
  /** Keep raw bytes alongside decoded views. */
  preserveRaw: boolean;
  /** Decode part bodies (text + binary). */
  decodeBodies: boolean;
  /** Walk the tree to surface attachments. */
  extractAttachments: boolean;
  /** Parse + sanitize HTML bodies. */
  parseHtml: boolean;
  /** Parse iCalendar parts. */
  parseCalendar: boolean;
  /** Parse vCard / xCard / jCard parts. */
  parseContacts: boolean;
  /** Parse DSN / MDN / ARF / DMARC / TLSRPT report parts. */
  parseReports: boolean;
  /** Parse S/MIME + OpenPGP crypto parts. */
  parseCrypto: boolean;
  /** Network reachability policy for verification stages. */
  externalNetwork: ExternalNetworkPolicy;
}

/**
 * Default {@link ParserConfig}. Permissive feature toggles, bounded
 * resource limits. See the spec's v3 §05 for the rationale; the
 * numbers here are the "containment for adversarial inputs" tier.
 */
export const DEFAULT_PARSER_CONFIG: ParserConfig = {
  maxMessageBytes: 50 * 1024 * 1024,
  maxHeaderBytes: 1 * 1024 * 1024,
  maxHeaderCount: 10_000,
  maxMimeDepth: 50,
  maxMimePartCount: 10_000,
  maxDecodedPartBytes: 50 * 1024 * 1024,
  maxAttachmentBytes: 50 * 1024 * 1024,
  maxArchiveDepth: 3,
  maxEmbeddedDepth: 10,
  preserveRaw: true,
  decodeBodies: true,
  extractAttachments: true,
  parseHtml: true,
  parseCalendar: true,
  parseContacts: true,
  parseReports: true,
  parseCrypto: true,
  externalNetwork: "disabled",
};

/** localStorage key for the user-edited {@link ParserConfig}. */
export const PARSER_CONFIG_STORAGE_KEY = "emlx:parser-config";

/**
 * Read the persisted {@link ParserConfig} merged onto
 * {@link DEFAULT_PARSER_CONFIG}. Defensive against malformed JSON,
 * missing keys, and stale shapes — anything that isn't a finite number
 * (for limit fields) or a known enum (for `externalNetwork`) falls
 * back to the default.
 */
export function readParserConfig(
  storage: Pick<Storage, "getItem"> | null = typeof localStorage === "undefined"
    ? null
    : localStorage,
): ParserConfig {
  if (!storage) return { ...DEFAULT_PARSER_CONFIG };
  let raw: string | null;
  try {
    raw = storage.getItem(PARSER_CONFIG_STORAGE_KEY);
  } catch {
    return { ...DEFAULT_PARSER_CONFIG };
  }
  if (!raw) return { ...DEFAULT_PARSER_CONFIG };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_PARSER_CONFIG };
  }
  if (!parsed || typeof parsed !== "object") {
    return { ...DEFAULT_PARSER_CONFIG };
  }
  const obj = parsed as Record<string, unknown>;
  const merged: ParserConfig = { ...DEFAULT_PARSER_CONFIG };
  for (const k of NUMERIC_LIMIT_KEYS) {
    const v = obj[k];
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) {
      merged[k] = v;
    }
  }
  for (const k of BOOLEAN_FEATURE_KEYS) {
    const v = obj[k];
    if (typeof v === "boolean") merged[k] = v;
  }
  if (
    obj.externalNetwork === "disabled" ||
    obj.externalNetwork === "dns-only" ||
    obj.externalNetwork === "allowlisted"
  ) {
    merged.externalNetwork = obj.externalNetwork;
  }
  return clampParserConfig(merged);
}

/**
 * Apply {@link MAX_PARSER_LIMITS} to every numeric limit. The single
 * place the ceiling is applied: the read goes through it, the write
 * goes through it, and Settings goes through it before it puts a
 * number in the field.
 *
 * That is the point rather than tidiness. The ceiling used to live
 * inside `readParserConfig` alone, so the device could hold 1000 MB
 * while the parser used 256 and the Settings field showed the 1000 it
 * had written. A reader raising a cap to open a large mailbox was
 * overruled three modules away from the control they had just used,
 * and the one number they would check to understand the refusal was
 * the wrong one.
 *
 * Clamped, not rejected: a reader who asks for more than the ceiling
 * still gets the ceiling, which is closer to their intent than
 * silently reverting them to the default would be. See
 * {@link MAX_PARSER_LIMITS} for why an unbounded raise cannot be
 * honoured at all.
 */
export function clampParserConfig(config: ParserConfig): ParserConfig {
  const clamped: ParserConfig = { ...config };
  for (const k of NUMERIC_LIMIT_KEYS) {
    clamped[k] = Math.min(config[k], MAX_PARSER_LIMITS[k]);
  }
  return clamped;
}

/**
 * The numeric limit keys that were reduced when `config` was clamped,
 * so a control can name what it was held to instead of appearing to
 * mistype the reader's number back at them.
 */
export function cappedLimitKeys(config: ParserConfig): NumericLimitKey[] {
  return NUMERIC_LIMIT_KEYS.filter((k) => config[k] > MAX_PARSER_LIMITS[k]);
}

/**
 * Persist a {@link ParserConfig}, clamped, and REPORT.
 *
 * Two changes from the version that returned `void`. The config is put
 * through {@link clampParserConfig} first, so the device cannot hold a
 * number the parser will refuse to use. And the failure is classified
 * and returned instead of swallowed behind "defaults stay in effect",
 * which was true of the parse and false of the surface: Settings told
 * the reader the limits were "stored in this browser" whether or not a
 * byte of them had been.
 *
 * The two ways to fail are told apart exactly as {@link writeEnvelope}
 * tells them apart. `{ ok: false }` with NO `error` means this browser
 * stores nothing for this site (private mode, blocked site data, SSR):
 * nothing was ever going to be kept, so there is nothing to blame.
 * `{ ok: false, error }` means the storage was there and refused.
 *
 * Reports through the toast bridge by default so a new call site is
 * loud without remembering to be. Settings passes `report: false`,
 * because it answers at the control itself and the same failure must
 * not be announced twice.
 */
export function writeParserConfig(
  config: ParserConfig,
  storage: Pick<Storage, "setItem"> | null = typeof localStorage === "undefined"
    ? null
    : localStorage,
  opts: { report?: boolean } = {},
): StorageWriteResult {
  if (!storage) return { ok: false };
  try {
    storage.setItem(PARSER_CONFIG_STORAGE_KEY, JSON.stringify(clampParserConfig(config)));
    return { ok: true };
  } catch (err) {
    const error = classifyStorageError(err);
    errorLog("writeParserConfig", err, { kind: error.kind });
    if (opts.report !== false) emitStorageToast(error.kind, PARSER_CONFIG_STORAGE_KEY);
    return { ok: false, error };
  }
}

/**
 * Hard ceilings for the numeric limits, applied to anything read out of
 * storage.
 *
 * Settings lets a reader raise these, which is legitimate: a forensic operator
 * with a 200 MB mailbox should be able to say so. What is not legitimate is an
 * unbounded raise. The persisted value only had to be a finite number `>= 0`,
 * so a stale, hand-edited or corrupted entry could set `maxMimePartCount` to a
 * billion and turn every containment limit below into a no-op. These caps are
 * roughly five times the defaults: high enough that no honest raise hits them,
 * low enough that the parser's guarantees survive a hostile config.
 */
export const MAX_PARSER_LIMITS = {
  maxMessageBytes: 256 * 1024 * 1024,
  maxHeaderBytes: 16 * 1024 * 1024,
  maxHeaderCount: 100_000,
  maxMimeDepth: 200,
  maxMimePartCount: 100_000,
  maxDecodedPartBytes: 256 * 1024 * 1024,
  maxAttachmentBytes: 256 * 1024 * 1024,
  maxArchiveDepth: 16,
  maxEmbeddedDepth: 64,
} as const;

/** Numeric-limit fields on {@link ParserConfig}. Used by readParserConfig. */
export const NUMERIC_LIMIT_KEYS = [
  "maxMessageBytes",
  "maxHeaderBytes",
  "maxHeaderCount",
  "maxMimeDepth",
  "maxMimePartCount",
  "maxDecodedPartBytes",
  "maxAttachmentBytes",
  "maxArchiveDepth",
  "maxEmbeddedDepth",
] as const satisfies readonly (keyof ParserConfig)[];

/** One numeric-limit field name. Every key here is in MAX_PARSER_LIMITS. */
export type NumericLimitKey = (typeof NUMERIC_LIMIT_KEYS)[number];

/** Boolean feature toggles on {@link ParserConfig}. */
export const BOOLEAN_FEATURE_KEYS = [
  "preserveRaw",
  "decodeBodies",
  "extractAttachments",
  "parseHtml",
  "parseCalendar",
  "parseContacts",
  "parseReports",
  "parseCrypto",
] as const satisfies readonly (keyof ParserConfig)[];
